import { XCircleIcon } from "@heroicons/react/24/outline";
import { get } from "lodash-es";
import { Dispatch, SetStateAction } from "react";
import {
	FieldErrors,
	FieldValues,
	Path,
	useController,
	UseControllerProps,
	UseFormRegister,
} from "react-hook-form";

import {
	cn,
	formatFileSizesWithPrefix,
	getFileTypeFromFileName,
} from "../../../lib";
import { ErrorMessage } from "./error-message";
import { FileIcon } from "./file-icon";
import { InputButton } from "./input-button";

interface FileUploadProps<TFormValues extends FieldValues>
	extends UseControllerProps<TFormValues> {
	name: Path<TFormValues>;
	register?: UseFormRegister<TFormValues>;
	errors?: FieldErrors<TFormValues>;
	setFileList: Dispatch<SetStateAction<File[]>>;
	fileList: File[];
	className?: string;
	buttonText?: string;
	buttonClassName?: string;
	maxFiles?: number;
	disabled?: boolean;
	accept?: string;
	showFileIcon?: boolean;
	showFileSize?: boolean;
}

/** What's different from a-prime FormUploadSectionComponent:
 * 1. Error message is below Click to add file button
 * 2. Do not have title (text above FILENAME)
 * 3. Error message wrapper is <p> instead of react-fragment
 * 4. Optionally takes in className for main container
 * 5. Hide input button if fileList is >= maxFiles
 */
export function FileUpload<TFormValues extends Record<string, unknown>>({
	name,
	register,
	errors,
	control,
	defaultValue,
	setFileList,
	fileList,
	className,
	maxFiles,
	disabled,
	buttonText,
	buttonClassName,
	accept,
	showFileIcon = true,
	showFileSize = true,
}: FileUploadProps<TFormValues>) {
	const { field } = useController({ name, control, defaultValue });
	const hideInputButton =
		maxFiles != undefined ? fileList.length >= maxFiles : false;

	const errorMessages = get(errors, name);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChange = (event: any) => {
		const incoming = Array.from(event.target.files ?? []) as File[];
		const merged = [...fileList, ...incoming];
		const updatedList =
			maxFiles != undefined ? merged.slice(0, maxFiles) : merged;
		field.onChange(updatedList);
		setFileList(updatedList);
	};

	const fileRemove = (file: File) => {
		const updatedList = [...fileList];
		updatedList.splice(fileList.indexOf(file), 1);
		field.onChange(updatedList);
		setFileList(updatedList);
	};

	return (
		<div className={cn("mt-6", className)}>
			<div className="mb-4 text-xs font-semibold text-[#68737D]">
				FILENAME
				<hr className="mt-4 h-0.5" />
				{fileList.map((file, index) => {
					const fileExtension = getFileTypeFromFileName(file.name);
					return (
						<div
							className={cn("flex items-center gap-2 text-left font-light", {
								"hover:bg-slate-100": !disabled,
							})}
							key={file.name + index}
						>
							{showFileIcon ? (
								<div className="shrink-0">
									<FileIcon extension={fileExtension} />
								</div>
							) : null}
							<p className="min-w-0 flex-1 truncate pl-1 text-left text-sm font-normal text-black sm:pl-0">
								{file.name}
							</p>
							{showFileSize ? (
								<p className="shrink-0 text-right text-sm font-normal text-black">
									{formatFileSizesWithPrefix(file.size)}
								</p>
							) : null}
							<button
								type="button"
								disabled={disabled}
								aria-label={`Remove ${file.name}`}
								className={cn(
									"flex h-10 w-6 shrink-0 items-center justify-end",
									disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
								)}
								onClick={() => {
									if (!disabled) fileRemove(file);
								}}
							>
								<XCircleIcon className="size-5 text-black" />
							</button>
						</div>
					);
				})}
			</div>
			{!hideInputButton && (
				<InputButton
					register={register}
					handleChange={handleChange}
					name={name}
					buttonText={buttonText}
					buttonClassName={buttonClassName}
					accept={accept}
					multiple={maxFiles === undefined || maxFiles > 1}
					disabled={disabled}
				/>
			)}
			{errorMessages && <ErrorMessage fieldError={errorMessages} />}
		</div>
	);
}
